import { Form } from '@/common/form/Form';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { FormFields } from '../form/rendering/FormFields';
import { DialogFormFieldContainer } from './DialogFormFieldContainer';

export const SubDialog = ({ data, closeDialog }) => {
  const rsrc = useRsrc();
  const module = useModuleFromRoute();

  const [pluginInfos, setPluginInfos] = useState();
  const [selectedPluginInfo, setSelectedPluginInfo] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState(null); // Store uploaded file

  const formRef = useRef();
  const fileInputRef = useRef(null); // Reference to the hidden file input

  // Fetch all PluginInfos
  useEffect(() => {
    const reqParam = {
      uiType: data.uiType,
      entity: data.entity,
    };

    rsrc.data(module, 'SUB_PLUGIN_INFO_DATA_PLUGIN', reqParam).then((res) => {
      setPluginInfos(res);
      if (res.length > 0) setSelectedPluginInfo(res[0]);
    });
  }, []);

  // If the selectedPluginInfo changed, try to fill them from the file
  useEffect(() => {
    if (!selectedFiles) return;
    fillFormFields();
  }, [selectedPluginInfo]);

  // When a new file is uploaded, try to select the applicable Plugin
  useEffect(() => {
    if (!selectedFiles) {
      return;
    }
    let fileParts = selectedFiles[0]?.name.split('.');

    // At this size, fields cannot apply
    if (fileParts.length <= 2) return;

    const fileSubPluginCode = fileParts[fileParts.length - 2] + '_SUB_PLUGIN';

    // If this Plugin is already selected, skip to filling the FieldDefs in the form
    // Additionally, if no Plugin matches the fileSubPluginCode, assume the pluginCode is not part of the fileName and fill anyway
    if (
      fileSubPluginCode === selectedPluginInfo.code ||
      pluginInfos.filter((pluginInfo) => pluginInfo.code === fileSubPluginCode).length === 0
    ) {
      fillFormFields();
      return;
    }

    setSelectedPluginInfo(pluginInfos.find((pluginInfo) => pluginInfo.code === fileSubPluginCode));
  }, [selectedFiles]);

  const fillFormFields = () => {
    let fileParts = selectedFiles[0]?.name.split('.');
    if (fileParts.length <= 2) return; // At this size, FieldDefs cannot apply

    const formValues = selectedPluginInfo.fieldDefs.reduce((acc, fieldDef, index) => {
      acc[fieldDef.field] = fileParts[index];
      return acc;
    }, {});

    formRef.current.setFormValues(formValues);
  };

  // Handles the user's click to upload a File
  const handleUploadClick = () => {
    fileInputRef.current.click(); // Triggers the hidden file input
  };

  // Handles the user's click to start the Sub
  const submit = () => {
    if (formRef.current.hasErrorState()) {
      formRef.current.touchAll();
      return;
    }

    const formValues = formRef.current.formValues;

    const subReq = {
      ...data,
      ...formValues,
    };

    const formData = new FormData();
    for (let i = 0; i < selectedFiles.length; i++) {
      formData.append('files', selectedFiles[i]);
    }

    // upload endpoint should be able to handle multiple files, returning a path of the files (instead of the filepath)
    rsrc.upload(module, selectedPluginInfo.code, formData, subReq.entity).then((uploadPath) => {
      subReq.uploadPath = uploadPath;

      const job = {
        code: selectedPluginInfo.code.replace('_SUB_PLUGIN', ''),
        name: formValues.name,
        module: module,
        entity: subReq.entity,
        entityId: subReq.entityId,
        rsrcCode: selectedPluginInfo.code,
        reqParamJson: JSON.stringify(subReq),
        cat: 'SUB',
      };

      rsrc.save('skdr', 'JOB', job).then((job) => {
        rsrc.start(job.id); // edit runSubJob to handle multiple files
        closeDialog();
      });
    });
  };

  return (
    <Dialog onClose={() => closeDialog()} open fullWidth>
      <DialogTitle>Submit</DialogTitle>

      <DialogContent>
        {/* File Upload Icon Button */}
        <Tooltip title="Upload File" placement="bottom-start">
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '8px',
            }}
            onClick={handleUploadClick}
          >
            <IconButton sx={{ color: '#000000', mr: 1 }}>
              <FontAwesomeIcon icon={fas.faFileUpload} />
            </IconButton>
            {!selectedFiles && <Box>Click to select a file to upload</Box>}
            {selectedFiles && (
              <span style={{ marginLeft: '10px' }}>
                {Array.from(selectedFiles)
                  .slice(0, 3)
                  .map((file) => file.name)
                  .join(', ')}
                {selectedFiles.length > 3 && `, +${selectedFiles.length - 3} more`}
              </span>
            )}
          </Box>
        </Tooltip>

        {/* Hidden File Input */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={(event) => setSelectedFiles(event.target.files)}
          multiple
        />

        {/* Plugin Select */}
        {pluginInfos && (
          <FormControl fullWidth sx={{ marginBottom: '1rem' }}>
            <InputLabel>Submission</InputLabel>
            <Select value={selectedPluginInfo} onChange={(event) => setSelectedPluginInfo(event.target.value)}>
              {pluginInfos.map((pluginInfo) => (
                <MenuItem key={pluginInfo.code} value={pluginInfo}>
                  {pluginInfo.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Plugin field definition form */}
        <Form
          key={selectedPluginInfo?.code} // Reset form when plugin changes to enable defaultValues to work
          ref={formRef}
        >
          <FormFields fieldDefs={selectedPluginInfo?.fieldDefs} container={DialogFormFieldContainer} />
        </Form>
      </DialogContent>

      {/* Bottom Buttons */}
      <DialogActions>
        <Button onClick={() => closeDialog()} variant="outlined" color="secondary">
          Cancel
        </Button>
        <Button onClick={submit} variant="contained" color="primary">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};
